import { permissionType } from './constants'
import { useUser } from '../contexts/UserContext'
import { JourneyStep } from '../hooks/useNavigation'
import { checkoutSections, cartSections, pdpSections, shopPassSections } from '../../components/navigation/nav-items'
import { IconProps } from '@phosphor-icons/react'
import { UserType } from '../types/user'
import { SearchItem } from '../../components/navigation/search-items'
import { ExtendedSearchItem } from 'src/components/navigation/SearchComponent'
import { NavigationItem } from '../../components/navigation/PrimaryNavBarComponents/PrimaryTabs'
import { OnboardingStep } from '../types/onboardingV2'

import {
  NavigationSectionType,
  NavigationItemType
} from '../../components/navigation/SecondaryNavBars/common/DynamicNavigation'

export interface NavigationSectionWithItems {
  title: string
  items: NavigationItemType[]
}

const excludedRBACRoutes = ['/cart/real-estate', '/sso/real-estate', '/ads-iq/real-estate', '/__copilot_dev']

const getDynamicRouteParent = (currentPath: string, accessibleNavItems: NavigationItem[]): string | null => {
  const matchingNavItem = accessibleNavItems.find(
    navItem => currentPath.startsWith(navItem.path + '/') && currentPath !== navItem.path
  )

  return matchingNavItem?.path || null
}

export const hasPermissionToTab = (user: UserType | undefined, rbacPermission: string | string[] | null): boolean => {
  if (!rbacPermission || (Array.isArray(rbacPermission) && rbacPermission.length === 0)) {
    return true
  }

  const requiredPermissions = Array.isArray(rbacPermission) ? rbacPermission : [rbacPermission]

  const isSuperAdmin = user?.isSuperAdmin || false
  const isAnalyticsTabAndSuperAdmin = requiredPermissions.includes(permissionType.TAB_ANALYTICS) && isSuperAdmin
  // Copilot tab: visible to anyone provisioned TAB_COPILOT, OR to Shopflo super
  // admins (isSuperAdmin === id 'super-admin', internal-only — NOT user.isAdmin,
  // which is true for every merchant org owner/admin-role holder, ~2k orgs).
  // Independent of TAB_ANALYTICS — the two super-admin carve-outs are separate.
  const isCopilotTabAndSuperAdmin = requiredPermissions.includes(permissionType.TAB_COPILOT) && isSuperAdmin
  const hasPermissionToTab =
    user?.permissions?.some((permission: any) => requiredPermissions.includes(permission.id)) ||
    isAnalyticsTabAndSuperAdmin ||
    isCopilotTabAndSuperAdmin

  return Boolean(hasPermissionToTab)
}

export const filterSearchItemsByRBAC = ({
  searchItems,
  user,
  completedSteps,
  isShopConnected
}: {
  searchItems: SearchItem[]
  user: UserType | undefined
  completedSteps: OnboardingStep[]
  isShopConnected: boolean
}): SearchItem[] => {
  const filterItems = (items: SearchItem[]): SearchItem[] => {
    return items
      .filter(item => {
        const hasItemPermission = hasPermissionToTab(user, item.rbacPermission)

        if (item.children && item.children.length > 0) {
          const filteredChildren = filterItems(item.children)
          return hasItemPermission || filteredChildren.length > 0
        }

        if (
          !completedSteps.includes(OnboardingStep.pg_setup) &&
          !isShopConnected &&
          item.path === '/settings/payments'
        ) {
          return false
        }

        return hasItemPermission
      })
      .map(item => {
        if (item.children && item.children.length > 0) {
          const filteredChildren = filterItems(item.children)
          return {
            ...item,
            children: filteredChildren
          }
        }
        return item
      })
  }

  return filterItems(searchItems)
}

export const filterNavigationItemsByPermission = (
  navigationItems: NavigationItem[],
  user: UserType | undefined,
  includeHidden: boolean = false
): NavigationItem[] => {
  return navigationItems
    .filter(item => {
      const hasPermission = hasPermissionToTab(user, item.rbacPermission)

      if (item.children && item.children.length > 0) {
        const filteredChildren = item.children.filter(child => hasPermissionToTab(user, child.rbacPermission))

        return hasPermission || filteredChildren.length > 0
      }

      return hasPermission
    })
    .map(item => {
      if (item.children && item.children.length > 0) {
        const filteredChildren = item.children.filter(child => hasPermissionToTab(user, child.rbacPermission))

        // If parent has no permission but has children with permissions,
        // replace parent with the first child
        if (!hasPermissionToTab(user, item.rbacPermission) && filteredChildren.length > 0) {
          const firstChild = filteredChildren[0]
          return {
            ...firstChild,
            children: filteredChildren.slice(1)
          }
        }

        return {
          ...item,
          children: filteredChildren
        }
      }

      return item
    })
    .filter(item => {
      if (item.conditionallyHidden) {
        return false
      }

      return !item.hidden || includeHidden
    })
}

export const filterNavigationSectionsByPermission = (
  sections: NavigationSectionType[],
  user: UserType | undefined
): NavigationSectionType[] => {
  return sections
    .map(section => ({
      ...section,
      items: section.items.filter(item => hasPermissionToTab(user, item.rbacPermission))
    }))
    .filter(section => section.items.length > 0)
}

export const getCurrentPathRBACPermission = (
  accessibleNavItems: NavigationItem[],
  currentPath: string
): NavigationItem | undefined => {
  const exactMatch = accessibleNavItems.find(item => currentPath === item.path)
  if (exactMatch) {
    return exactMatch
  }

  const childMatch = accessibleNavItems.find(item => {
    if (item.children && item.children.length > 0) {
      return item.children.some(child => currentPath === child.path)
    }
    return false
  })
  if (childMatch) {
    return childMatch
  }

  const dynamicParentPath = getDynamicRouteParent(currentPath, accessibleNavItems)
  if (dynamicParentPath) {
    const dynamicParentMatch = accessibleNavItems.find(item => item.path === dynamicParentPath)
    if (dynamicParentMatch) {
      return dynamicParentMatch
    }
  }

  return accessibleNavItems.find(item => {
    const path = item.path.split('/')[1]
    return currentPath.includes(path)
  })
}

export const getInitialAccessibleTabPath = (accessibleNavItems: NavigationItem[]): string => {
  if (!accessibleNavItems.length) return '/'

  const firstItem = accessibleNavItems[0]
  return firstItem.path
}

export const shouldRedirectUser = (
  currentPath: string,
  accessibleNavItems: NavigationItem[],
  user: UserType | undefined
): { shouldRedirect: boolean; redirectPath: string } => {
  const currPathRBACPermission = getCurrentPathRBACPermission(accessibleNavItems, currentPath)

  if (excludedRBACRoutes.includes(currentPath)) {
    return {
      shouldRedirect: false,
      redirectPath: ''
    }
  }

  if (!currPathRBACPermission) {
    return {
      shouldRedirect: true,
      redirectPath: getInitialAccessibleTabPath(accessibleNavItems)
    }
  }

  const hasPermissionToCurrentTab = hasPermissionToTab(user, currPathRBACPermission.rbacPermission)

  const isSuperAdmin = user?.isSuperAdmin || false
  if (currPathRBACPermission.rbacPermission === permissionType.TAB_ANALYTICS && isSuperAdmin) {
    return { shouldRedirect: false, redirectPath: '' }
  }

  if (!hasPermissionToCurrentTab) {
    return {
      shouldRedirect: true,
      redirectPath: getInitialAccessibleTabPath(accessibleNavItems)
    }
  }

  return { shouldRedirect: false, redirectPath: '' }
}

const createRouteToSectionMap = () => {
  const routeMap = new Map<string, JourneyStep>()

  checkoutSections.forEach(section => {
    section.items.forEach(item => {
      routeMap.set(item.path, JourneyStep.CHECKOUT)
    })
  })

  cartSections.forEach(section => {
    section.items.forEach(item => {
      routeMap.set(item.path, JourneyStep.CART)
    })
  })

  pdpSections.forEach(section => {
    section.items.forEach(item => {
      routeMap.set(item.path, JourneyStep.PDP)
    })
  })

  shopPassSections.forEach(section => {
    section.items.forEach(item => {
      routeMap.set(item.path, JourneyStep.SHOP_PASS)
    })
  })

  return routeMap
}

const routeToSectionMap = createRouteToSectionMap()

export const getJourneyStepFromPath = (currentPath: string, user?: UserType | undefined): JourneyStep => {
  const allSecondarySections = [
    ...checkoutSections.flatMap((section: NavigationSectionWithItems) => section.items),
    ...cartSections.flatMap((section: NavigationSectionWithItems) => section.items),
    ...pdpSections.flatMap((section: NavigationSectionWithItems) => section.items),
    ...shopPassSections.flatMap((section: NavigationSectionWithItems) => section.items)
  ]

  const matchingSecondaryRoute = allSecondarySections.find((item: NavigationItemType) =>
    currentPath.startsWith(item.path)
  )

  if (matchingSecondaryRoute) {
    if (user && !hasPermissionToTab(user, matchingSecondaryRoute.rbacPermission)) {
      return JourneyStep.NONE
    }

    const journeyStep = routeToSectionMap.get(matchingSecondaryRoute.path)
    if (journeyStep) {
      return journeyStep
    }
  }

  return JourneyStep.NONE
}

export const filterSearchItems = (items: SearchItem[], searchTerm: string): ExtendedSearchItem[] => {
  return items
    .filter(item => {
      const matchesSearch =
        item.title.toLowerCase().includes(searchTerm) ||
        item.searchText.some(keyword => keyword.toLowerCase().includes(searchTerm))

      const hasMatchingChildren = item.children?.some(child => filterSearchItems([child], searchTerm).length > 0)

      return matchesSearch || hasMatchingChildren
    })
    .map(item => {
      if (!item.children) return item

      const filteredChildren = filterSearchItems(item.children, searchTerm)
      const parentMatches =
        item.title.toLowerCase().includes(searchTerm) ||
        item.searchText.some(keyword => keyword.toLowerCase().includes(searchTerm))

      if (!parentMatches && filteredChildren.length > 0) {
        return { ...item, children: filteredChildren, _hideParent: true }
      }

      return { ...item, children: filteredChildren }
    })
}

export const flattenSearchItems = (items: ExtendedSearchItem[]): ExtendedSearchItem[] => {
  const result: ExtendedSearchItem[] = []

  items.forEach(item => {
    if (!item._hideParent) {
      result.push(item)
    }
    if (item.children) {
      result.push(...flattenSearchItems(item.children))
    }
  })

  return result
}

export const getNextAccessiblePathInJourneyStep = (journeyStep: JourneyStep, user: UserType | undefined): string => {
  let sections: NavigationSectionType[] = []

  switch (journeyStep) {
    case JourneyStep.CHECKOUT:
      sections = checkoutSections
      break
    case JourneyStep.CART:
      sections = cartSections
      break
    case JourneyStep.PDP:
      sections = pdpSections
      break
    case JourneyStep.SHOP_PASS:
      sections = shopPassSections
      break
    default:
      return '/'
  }

  const allItems = sections.flatMap(section => section.items)

  const accessibleItems = allItems.filter(item => hasPermissionToTab(user, item.rbacPermission))

  return accessibleItems?.[0]?.path || '/'
}

export const useNavigationRBAC = () => {
  const { user } = useUser()

  const filterItems = (items: NavigationItem[], includeHidden: boolean = false) => {
    return filterNavigationItemsByPermission(items, user, includeHidden)
  }

  const filterSections = (sections: NavigationSectionType[]) => {
    return filterNavigationSectionsByPermission(sections, user)
  }

  const hasPermission = (rbacPermission: string) => {
    return hasPermissionToTab(user, rbacPermission)
  }

  const checkRedirect = (currentPath: string, accessibleNavItems: NavigationItem[]) => {
    return shouldRedirectUser(currentPath, accessibleNavItems, user)
  }

  const getJourneyStep = (currentPath: string): JourneyStep => {
    return getJourneyStepFromPath(currentPath, user)
  }

  const getNextAccessiblePath = (journeyStep: JourneyStep, userData: UserType | undefined): string => {
    return getNextAccessiblePathInJourneyStep(journeyStep, userData)
  }

  return {
    filterItems,
    filterSections,
    hasPermission,
    checkRedirect,
    getJourneyStep,
    getNextAccessiblePath,
    user
  }
}
